import { Alert, Platform } from 'react-native';

interface AlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertOptions {
  cancelable?: boolean;
}

/**
 * Cross-platform alert that works on iOS, Android, and Web
 * On web, uses browser's native confirm/alert dialogs
 */
export function platformAlert(
  title: string,
  message?: string,
  buttons?: AlertButton[],
  options?: AlertOptions
): void {
  if (Platform.OS === 'web') {
    // Web implementation using browser dialogs
    if (!buttons || buttons.length === 0) {
      // Simple alert with no buttons
      window.alert(message ? `${title}\n\n${message}` : title);
      return;
    }

    if (buttons.length === 1) {
      // Single button - just show alert and call onPress
      window.alert(message ? `${title}\n\n${message}` : title);
      buttons[0].onPress?.();
      return;
    }

    if (buttons.length === 2) {
      // Two buttons - use confirm dialog
      // Find the "action" button (not cancel)
      const cancelButton = buttons.find(b => b.style === 'cancel');
      const actionButton = buttons.find(b => b.style !== 'cancel') || buttons[1];
      
      const result = window.confirm(message ? `${title}\n\n${message}` : title);
      
      if (result) {
        // User clicked OK - trigger action button
        actionButton?.onPress?.();
      } else {
        // User clicked Cancel
        cancelButton?.onPress?.();
      }
      return;
    }

    // More than 2 buttons - show options in prompt (fallback)
    // This is a limitation of web - we can only do confirm/alert natively
    // For complex cases, show a confirm with the primary action
    const destructiveButton = buttons.find(b => b.style === 'destructive');
    const cancelButton = buttons.find(b => b.style === 'cancel');
    const primaryButton = destructiveButton || buttons.find(b => b.style !== 'cancel') || buttons[0];
    
    const buttonLabels = buttons.filter(b => b.style !== 'cancel').map(b => b.text).join(' / ');
    const confirmMessage = message 
      ? `${title}\n\n${message}\n\nClick OK for: ${primaryButton?.text}`
      : `${title}\n\nClick OK for: ${primaryButton?.text}`;
    
    const result = window.confirm(confirmMessage);
    
    if (result) {
      primaryButton?.onPress?.();
    } else {
      cancelButton?.onPress?.();
    }
    return;
  }

  // Native implementation - use React Native Alert
  Alert.alert(title, message, buttons, options);
}

/**
 * Simple alert with just OK button
 */
export function showAlert(title: string, message?: string, onOk?: () => void): void {
  platformAlert(title, message, [{ text: 'OK', onPress: onOk }]);
}

/**
 * Confirmation dialog with Cancel and Confirm buttons
 */
export function showConfirm(
  title: string,
  message: string,
  onConfirm: () => void,
  onCancel?: () => void,
  confirmText: string = 'Confirm',
  cancelText: string = 'Cancel'
): void {
  platformAlert(title, message, [
    { text: cancelText, style: 'cancel', onPress: onCancel },
    { text: confirmText, onPress: onConfirm },
  ]);
}

/**
 * Destructive confirmation (e.g., for delete actions)
 */
export function showDestructiveConfirm(
  title: string,
  message: string,
  onConfirm: () => void,
  onCancel?: () => void,
  confirmText: string = 'Delete',
  cancelText: string = 'Cancel'
): void {
  platformAlert(title, message, [
    { text: cancelText, style: 'cancel', onPress: onCancel },
    { text: confirmText, style: 'destructive', onPress: onConfirm },
  ]);
}

export default platformAlert;

